import { CheckIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  Select,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useData } from "../../../Contexts/DataContext";
import {
  deleteFilesFromUrls,
  uploadFile,
} from "../../../Utils/fileStorageUtils";
import ConfirmDialog from "../../Shared/ConfirmDialog";
import EditTaskModal from "./EditTaskModal";
import ViewTaskModal from "./ViewTaskModal";

const TaskPanel = ({ requirementId }) => {
  const {
    tasks,
    addDocument,
    updateDocument,
    deleteDocument,
    selectedClient,
    clientUsers,
  } = useData();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [hideCompleted, setHideCompleted] = useState(false);
  const [sortBy, setSortBy] = useState("date"); // options: "date", "assignedTo", "status"

  const filteredAndSortedTasks = useMemo(() => {
    return [...tasks]
      .filter((t) => t.requirementId === requirementId)
      .filter((t) => (hideCompleted ? !t.completed : true))
      .sort((a, b) => {
        if (sortBy === "date") return new Date(a.dueDate) - new Date(b.dueDate);
        if (sortBy === "assignedTo")
          return (a.assignedTo || "").localeCompare(b.assignedTo || "");
        if (sortBy === "status")
          return Number(a.completed) - Number(b.completed);
        return 0;
      });
  }, [tasks, requirementId, hideCompleted, sortBy]);

  // console.log("requirementId", requirementId)
  // console.log("filteredAndSortedTasks", filteredAndSortedTasks)

  const handleSave = async (task) => {
    if (!task.name || !task.dueDate || isSubmitting) return;
    const assignee = clientUsers.find((u) => u.id === task.assignedToId);

    setIsSubmitting(true);

    try {
      let uploadedScreenshots = [];

      if (task.screenshots?.length > 0) {
        uploadedScreenshots = await Promise.all(
          task.screenshots.map(async (file) => {
            if (file instanceof File) {
              return await uploadFile(
                file,
                `screenshots/tasks/${task.id || "temp"}`
              );
            }
            return file;
          })
        );
      }

      if (task.isNew) {
        const newTaskData = {
          name: task.name,
          dueDate: task.dueDate,
          detail: task.detail,
          completed: task.completed,
          requirementId,
          createdOn: task.createdOn,
          clientId: selectedClient?.id,
          assignedTo: assignee?.name || "",
          assignedToId: task.assignedToId || "",
          screenshots: [],
        };

        const savedId = await addDocument("tasks", newTaskData);

        await updateDocument("tasks", savedId, {
          screenshots: uploadedScreenshots,
        });
      } else {
        await updateDocument("tasks", task.id, {
          name: task.name,
          dueDate: task.dueDate,
          detail: task.detail,
          completed: task.completed,
          assignedTo: assignee?.name || "",
          assignedToId: task.assignedToId || "",
          screenshots: uploadedScreenshots,
        });
      }

      setEditingTask(null);
    } catch (error) {
      console.error("Error saving task:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const task = tasks.find((t) => t.id === taskId);

      if (!task) {
        console.warn("Task not found for deletion:", taskId);
        return;
      }

      // Delete screenshots from storage if present
      if (task.screenshots?.length > 0) {
        await deleteFilesFromUrls(task.screenshots);
      }

      // Delete from Firestore
      await deleteDocument("tasks", taskId);

      // Clear editing state if this task was being edited
      if (editingTask?.id === taskId) {
        setEditingTask(null);
      }
    } catch (err) {
      console.error("Error deleting task and files:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewTaskRow = () => {
    const newTaskId = `temp-${Date.now()}`;
    const newTask = {
      id: newTaskId,
      name: "",
      dueDate: "",
      detail: "",
      completed: false,
      requirementId,
      createdOn: new Date().toISOString(),
      isNew: true,
      screenshots: [],
      assignedTo: "",
    };
    setEditingTask(newTask);
  };

  return (
    <Box>
      <Flex justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        <HStack
          justifyContent="space-between"
          w="100%"
          alignItems="center"
          mb={4}
        >
          <HStack spacing={4} alignItems="center">
            <Text fontWeight="bold">Tasks</Text>

            <HStack spacing={2}>
              <Checkbox
                isChecked={hideCompleted}
                onChange={(e) => setHideCompleted(e.target.checked)}
                size="sm"
              >
                <Text fontSize="sm">Hide Completed</Text>
              </Checkbox>
            </HStack>

            <HStack spacing={2} alignItems="center">
              <Text fontSize="sm">Sort by:</Text>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                size="sm"
                w="auto"
              >
                <option value="date">Due Date</option>
                <option value="assignedTo">Assigned To</option>
                <option value="status">Status</option>
              </Select>
            </HStack>
          </HStack>

          <Button onClick={handleAddNewTaskRow} colorScheme="blue" size="sm">
            Add Task
          </Button>
        </HStack>
      </Flex>

      {filteredAndSortedTasks.length > 0 ? (
        <VStack spacing={2} align="stretch">
          {filteredAndSortedTasks.map((task, index) => (
            <Box
              key={task.id}
              p={3}
              borderWidth={1}
              borderRadius="md"
              bg={task.completed ? "white" : "white"}
              boxShadow="sm"
            >
              <Flex justify="space-between" align="center">
                <Box flex={3}>
                  <HStack spacing={2}>
                    {task.completed && <CheckIcon color="green.500" />}
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      cursor="pointer"
                      _hover={{ textDecoration: "underline" }}
                      onClick={() => setViewingTask(task)}
                      textDecoration={task.completed ? "line-through" : "none"}
                    >
                      {task.name}
                    </Text>
                  </HStack>

                  <HStack alignItems="center" spacing={3} mt={1}>
                    <Text fontSize="sm" color="gray.500">
                      🗓️{" "}
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString()
                        : "N/A"}
                    </Text>

                    <HStack spacing={1}>
                      <Text fontSize="sm" color="gray.500">
                        👤
                      </Text>
                      <HStack spacing={1}>
                        <Text fontSize="sm" color="gray.600">
                          {task.assignedTo || "Not Assigned"}
                        </Text>
                      </HStack>
                    </HStack>
                  </HStack>
                </Box>

                <Tag
                  borderRadius="sm"
                  size="sm"
                  colorScheme={task.completed ? "green" : "yellow"}
                  variant="subtle"
                >
                  {task.completed ? "✔ Completed" : "⏳ Pending"}
                </Tag>

                <HStack spacing={2} flex={1} justify="end">
                  <Button
                    leftIcon={<CheckIcon />}
                    size="xs"
                    colorScheme={task.completed ? "yellow" : "green"}
                    variant="outline"
                    onClick={() => {
                      setTaskToDelete({
                        id: task.id,
                        index,
                        toggleComplete: true,
                        newValue: !task.completed,
                      });
                      setShowDeleteDialog(true);
                    }}
                  >
                    {task.completed ? "Reopen" : "Mark Complete"}
                  </Button>

                  {!task.completed && (
                    <Button
                      leftIcon={<EditIcon />}
                      variant="outline"
                      size="xs"
                      onClick={() => setEditingTask(task)}
                    >
                      Edit
                    </Button>
                  )}
                </HStack>
              </Flex>
            </Box>
          ))}
        </VStack>
      ) : (
        <Text color="gray.500">No tasks found.</Text>
      )}

      {viewingTask && (
        <ViewTaskModal
          task={viewingTask}
          isOpen={!!viewingTask}
          onClose={() => setViewingTask(null)}
        />
      )}

      {editingTask && (
        <EditTaskModal
          isOpen={!!editingTask}
          task={editingTask}
          clientUsers={clientUsers}
          onClose={() => setEditingTask(null)}
          onSave={(updatedTask) => {
            handleSave(updatedTask);
            setEditingTask(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          if (taskToDelete) {
            const { id, toggleComplete, newValue } = taskToDelete;

            if (toggleComplete) {
              try {
                await updateDocument("tasks", id, {
                  completed: newValue,
                  modifiedOn: new Date().toISOString(),
                });
              } catch (err) {
                console.error("Error updating task completion state:", err);
              }
            } else {
              await handleDeleteTask(id);
            }

            setShowDeleteDialog(false);
            setTaskToDelete(null);
          }
        }}
        title={
          taskToDelete?.toggleComplete
            ? taskToDelete.newValue
              ? "Confirm Completion"
              : "Confirm Reopen"
            : "Confirm Task Deletion"
        }
        body={
          taskToDelete?.toggleComplete
            ? taskToDelete.newValue
              ? "Are you sure you want to mark this task as complete?"
              : "Are you sure you want to reopen this task?"
            : "Are you sure you want to delete this task? This cannot be undone."
        }
        confirmText={
          taskToDelete?.toggleComplete
            ? taskToDelete.newValue
              ? "Mark Complete"
              : "Reopen"
            : "Delete Task"
        }
        confirmColorScheme={
          taskToDelete?.toggleComplete
            ? taskToDelete.newValue
              ? "green"
              : "yellow"
            : "red"
        }
        cancelText="Cancel"
        isLoading={isSubmitting}
      />
    </Box>
  );
};

export default TaskPanel;
